import { toFixed } from "@/helpers/utils";
import { ORDER_STATUS } from "@/helpers/order/status";
import router from "../../router";
import { EMPLOYEE_ROLE } from "@/helpers/employee/role";
import calculateTaxes from "./tax/calculateTaxes";
// import scaleMixin from "../scaleMixin";
import syncMixin from "../syncMixin";
import cdOrderMixin from "../cdOrderMixin";
import { mapGetters, mapActions } from "vuex";
import { calculateItemTaxTotal, getItemGrandTotal, getItemSubtotal, getModSum, getOrderGrantTotal, getOrderSubTotal,distributeAmount,roundToTwo } from "@/helpers/calc";
import { computeSurcharge, computePointsContext, getModifierEffectivePrice } from "@/pricing";
// Removed unused promotion imports after switching to discount-only BXY handling
import printMixin from "../printMixin";
import { PAYMENT_BEHAVIOR } from "@/helpers/order/payment/behavior";
// import { TRANSACTION_TYPE } from "@/helpers/order/transaction/types";
import { Decimal } from 'decimal.js';
import { createOrderSyncCoordinator } from "@/helpers/order/orderSyncCoordinator";
import { getCashDiscountFactor, applyCashDiscountToItem, DEFAULT_CASH_DISCOUNT_FACTOR } from "@/helpers/order/cashDiscount";
import { normalizePromotionItemsForSync as normalizePromotionItems } from "@/helpers/order/promotionSyncNormalize";
import EventBus from "@/helpers/eventBus";

export default {
  data () {
    return {
      allowCashDiscount: null,
      cashDiscountFactor: DEFAULT_CASH_DISCOUNT_FACTOR,
      table: null
    };
  },
  mixins: [calculateTaxes, syncMixin, cdOrderMixin, printMixin],
  computed: {
    ...mapGetters({
      terminal: "config/terminal",
      terminalPaymentTypes: "config/paymentTypes",
      refundOrderId: 'orders/refundOrderId',
      isKioskMode: "config/isKioskMode",
      wholeOrderDiscount : "orders/wholeOrderDiscount",
      showSelectedOrder: 'orders/showSelectedOrder',
      refundedGiftCards: 'giftVoucher/refundedGiftCards',
      requestedPath: "orders/requestedPath",
      products: "config/products", // Need products to get tax rates
      isTaxInclusive: "config/isTaxInclusive" // Need to know tax mode
    }),
    orderDiningOption () {
      if (!this.terminal) return null;
      return this.terminal["dining_options"].find(option => this.currentOrder.dining_option_type === option.id);
    },
    itemsAtCurrentOrderLength () {
      return this.itemsAtCurrentOrder.length;
    },
    itemsAtCurrentOrder () {
      if (this.currentOrder && this.currentOrder.status === ORDER_STATUS.CANCELLED) {
        return this.currentOrder.items || [];
      }
      return (this.currentOrder.items || []).filter(function (item) {
        return item.void_status !== 1;
      });
    },
    isPaymentTypesAvailable () {
      return this.paymentTypes.length !== 0 || this.isKioskMode;
    },
    paymentTypes() {
      const cashDiscountSettingsEnabled = this.allowCashDiscount && this.allowCashDiscount.value === "true" && !this.isGiftCardRefund
      if (this.currentOrder && !this.isKioskMode) {
        if (cashDiscountSettingsEnabled && this.currentOrder.cash_discount)
          return this.terminalPaymentTypes.filter(paymentType => paymentType.name === 'Cash')
        else if (cashDiscountSettingsEnabled)
          return this.terminalPaymentTypes.filter(paymentType => paymentType.name !== 'Cash')
        else if (this.isGiftCardRefund)
          return this.terminalPaymentTypes.filter(paymentType => paymentType.behavior === PAYMENT_BEHAVIOR.GIFT_CARD)
        else
          return this.terminalPaymentTypes
      }
      return this.terminalPaymentTypes
    },
    isGiftCardRefund(){
      return this.refundedGiftCards.length > 0
    }
  },
  methods: {
    // Add an array of BXY free items to the order using a consistent structure

    ...mapActions('orders',['setRefundOrderId','setEmptyCurrentOrder','setActiveOrderState','setWholeOrderDiscount','setSelectedOrder']),
    ...mapActions('giftVoucher',['removeSelectedGiftCard', 'updateSelectedGiftCards','setRefundGiftCardIds']),
    // Single, lazily-created sync coordinator bound to this component's active
    // order. Replaces the old per-component debounce timers (pendingItemSyncTimeout,
    // queueSyncTimeout) and centralizes the localPersist -> enqueue -> drain
    // pipeline. See helpers/order/orderSyncCoordinator.js.
    getOrderSync () {
      if (!this._orderSync) {
        this._orderSync = createOrderSyncCoordinator({
          store: this.$store,
          getOrder: () => this.currentOrder,
          prepareOrder: () => this.normalizePromotionItemsForSync(),
          isOnline: () => navigator.onLine,
        });
      }
      return this._orderSync;
    },
    // Free promo lines (BXY / price === 0) must not carry tax into sync payloads.
    normalizePromotionItemsForSync () {
      try {
        if (this.currentOrder && Array.isArray(this.currentOrder.items)) {
          this.currentOrder.items = normalizePromotionItems(this.currentOrder.items);
        }
      } catch (e) {
        // fail safe: do not block sync
      }
    },
    scheduleOrderSync () {
      // Debounced local persist (IndexedDB + Ably). Backend queue sync happens
      // on leave/checkout/payment/status changes via the queue stage.
      this.getOrderSync().schedule({ queue: false });
    },
    sync: async function (countSubtotal = true, syncToBackend = false) {
      /**
       * Calculates taxes, subtotal, grandtotal discounts and saves order locally.
       * Full background sync (global `sync` action) runs only when syncToBackend=true.
       * Otherwise the coordinator's local pipeline persists locally only.
       */

      if(countSubtotal) {
        this.countSubtotal();
      }

      if (syncToBackend) {
        await this.getOrderSync().flush({ queue: true, trigger: "order_sync_backend" });
        return;
      }

      // Local pipeline: normalize -> persist, immediately.
      await this.getOrderSync().flush({ queue: false });
    },
    getItemPriceDisplay (item) {
      return toFixed(item.price, 2);
    },
    calculateItemCashDiscountChange (index, newItem = false) {
      const item = this.currentOrder.items[index];
      if (!item) return;

      // Idempotent price conversion (see helpers/order/cashDiscount). Adjusts the
      // item price plus each modifier's price AND price_original, so re-opening
      // the modifier modal never re-applies the discount and modifiers are
      // actually discounted in the totals.
      applyCashDiscountToItem(item, {
        factor: this.cashDiscountFactor,
        want: !!this.currentOrder.cash_discount,
        newItem,
      });

      item.tax_items = this.calculateItemTaxItems(item);
      item.tax = calculateItemTaxTotal(item) || 0;
    },
    async toggleCashDiscount () {
      if (!this.allowCashDiscount) return;
      const wasAdjusted = !!this.currentOrder.cash_discount;
      this.currentOrder.cash_discount = !wasAdjusted;
      this.currentOrder.items.forEach((item, index) => {
        // Gift-card products are excluded from cash discount.
        if (item.product === -2) return;
        // Stamp the pre-toggle state so each line flips exactly once, even legacy
        // rows that predate the cd_adjusted marker.
        item.cd_adjusted = wasAdjusted;
        (item.modifiers || []).forEach((mod) => { mod.cd_adjusted = wasAdjusted; });
        this.calculateItemCashDiscountChange(index);
      });
      // sync() recalculates order totals, so push to the customer display after
      // it finishes or the display receives stale subtotal/grand_total.
      const result = await this.sync();
      if (typeof this.sendOrderToChannel === 'function') {
        this.sendOrderToChannel(this.currentOrder);
      }
      return result;
    },
    getItemGrandTotal (item) {
      return getItemGrandTotal(item);
    },
    removeItemFromOrder: async function (itemId, skipConfirmation = false) {
      const self = this;

      // Kiosk mode never captures a void reason — keep the original lightweight flow.
      if (self.isKioskMode) {
        if (skipConfirmation) {
          await self.removeItemFromOrderProcessing(itemId);
          return true;
        }
        return self.confirmRemoveItemKiosk(itemId);
      }

      // Non-kiosk: a void reason is captured before an item can be removed, but
      // only if the store has configured reasons. If none are available, skip the
      // prompt and remove directly. The modal doubles as the removal confirmation
      // and is shown regardless of skipConfirmation (e.g. deleting from modifiers).
      if (!self.employee) return false;

      const availableReasons = self.$store.getters["config/voidReasons"] || [];
      let voidReason = null;
      if (availableReasons.length > 0) {
        voidReason = await self.promptVoidReason(self.$t("voidreason.void-item"));
        if (!voidReason) return false; // cancelled — abort the removal
      }

      // Cashiers must verify with a manager PIN before voiding.
      if (self.employee.role && self.employee.role === EMPLOYEE_ROLE.Cashier && !self.isVerified) {
        self.lockedAction = "remove-item";
        self.lockedItemId = itemId;
        self.lockedVoidReason = voidReason;
        return self.$bvModal.show("enter-pin-modal");
      }

      await self.removeItemFromOrderProcessing(itemId, voidReason);
      return true;
    },
    confirmRemoveItemKiosk (itemId) {
      const self = this;
      const h = this.$createElement;
      const messageVNode = h("div", { class: ["modal-complete"] }, [
        h("div", {
          domProps: {
            innerHTML: `'<svg class="icon icon-icon-popup-remove">
                <use xlink:href="#icon-icon-popup-remove"></use>
                </svg>
                <h2>${this.$t("removeitempopup.remove-this-item")}</h2>
                <span class="popup-meta">
                    ${this.$t("removeitempopup.are-you-sure-you-want-to-remove-this-item")}
                </span>`
          }
        })
      ]);
      try {

        return this.$bvModal.msgBoxConfirm([messageVNode], {
          id: "12345",
          title: [],
          centered: true,
          modalClass: "success-popup",
          hideHeader: true,
          footerClass: "d-flex justify-content-center align-items-center",
          cancelVariant: "outline-danger",
          okVariant: "danger",
          okTitle: "YES",
          "dialog-class": "qwerty2",
          cancelTitle: "NO",
          ststic: false,
          noFade: true
        }).then(async function (result) {
          if (result) {
            await self.removeItemFromOrderProcessing(itemId);
            return true;
          }
          return false;
        }).catch(err => {
          console.error(err);
        });
      } catch (e) {
        console.error(e);
      }
    },
    promptVoidReason (title) {
      // Returns a promise resolving to the chosen reason string, or null if the
      // staff member cancelled. Bridged to VoidReasonModal via the event bus.
      // Deferred a tick so that if another modal just closed (cancel confirm or
      // the "leave order" modal), its transition settles first — bootstrap-vue
      // can otherwise swallow a modal opened in the same frame another closes.
      return new Promise((resolve) => {
        this.$nextTick(() => {
          EventBus.$emit("void-reason:open", { resolve, title });
        });
      });
    },
    removeItemFromOrderProcessing (itemId, voidReason = null) {
      /**
       * This function is used to remove item from the order
       */
      const self = this;
      //get the index of the item to be removed
      const position = self.currentOrder.items.findIndex(currentItem => currentItem.id == itemId);
      // mark the item void_status as true
      self.currentOrder.items[position].void_status = 1;
      self.currentOrder.items[position].voided_at = new Date().toISOString();
      if (voidReason) {
        self.currentOrder.items[position].void_reason = voidReason;
      }
      self.removeSelectedGiftCard(self.currentOrder.items[position].local_id)

      // count all the essential paramters again
      self.countSubtotal();
      self.markUpdated(position, "item canceled");

      // Promotion re-evaluation is handled by the currentOrder.items deep watcher
      // in StandardOrder.vue via reEvaluatePromotions() — no legacy BXY logic here.

      if (this.isKioskMode) {
        this.amountTenderedString = this.currentOrderGrandTotalSum * 100 + "";
      }
      self.$forceUpdate();
      // sync the order to the backend
      self.scheduleOrderSync();
      if (this.currentOrder.discount_orders) {
        this.countSubtotal();
      }
    },
    countSubtotal: function () {
      /**
       * This function is responsible for calculating all the essential calculations like order's tax total, subtotal, grand total, discount total
       */
      this.currentOrder.subtotal = 0;
      this.currentOrder.discount_total = 0;

      // Auto-adjust Points Redeemed quantity if order total changed
      // Skip adjustment if we are currently loading data (to prevent race conditions)
      const pointsItem = this.currentOrder.items.find(item => item.name === 'Points Redeemed' && !item.void_status);
      if (pointsItem && this.currentOrder.customer && !this.isLoad) {
        // Subtotal-without-points and max-points-by-value now come from the
        // single pricing engine (src/pricing) so this matches every other
        // money-math code path.
        const rewardsConversionRate = this.rewardsConversionRate || 0.01;
        const pointsContext = computePointsContext(this.currentOrder, { rewardsConversionRate, isTaxInclusive: this.isTaxInclusive });
        const subtotalWithoutPoints = pointsContext.subtotalWithoutPoints;
        const maxPointsByValue = pointsContext.maxPointsByValue;

        // Check if we have valid points data
        const customer = this.currentOrder.customer;
        const hasPointsData = 'rewards_points' in customer || 'reward_points' in customer || 'points_balance' in customer;

        // Only enforce customer limit if we have the data
        let customerPoints = Infinity;
        if (hasPointsData) {
            customerPoints = customer.rewards_points || customer.reward_points || customer.points_balance || 0;
        }

        const maxAllowedPoints = Math.min(customerPoints, maxPointsByValue);

        // Get current redeemed points (absolute value since quantity is negative)
        const currentRedeemedPoints = Math.abs(pointsItem.quantity);

        // If current redemption exceeds max allowed, adjust it
        if (currentRedeemedPoints > maxAllowedPoints) {
          if (maxAllowedPoints <= 0) {
            // Remove the item if no points can be redeemed
            // BUT only if we are sure about the limits (i.e., not just missing data)
            // If subtotal is 0, we can definitely remove.
            // If customerPoints is 0 (and we have data), we can remove.
            if (subtotalWithoutPoints <= 0 || (hasPointsData && customerPoints <= 0)) {
                const itemIndex = this.currentOrder.items.findIndex(item => item.name === 'Points Redeemed' && !item.void_status);
                if (itemIndex !== -1) {
                  this.currentOrder.items.splice(itemIndex, 1);
                }
            }
          } else {
            // Adjust to max allowed
            const price = rewardsConversionRate;
            const quantity = -1 * maxAllowedPoints;
            pointsItem.quantity = quantity;
            pointsItem.price = price;
            pointsItem.price_base = price;
            pointsItem.subtotal = quantity * price;
            pointsItem.grand_total = quantity * price;
          }
        }
      }

      //calculate whole order discount
      this.applyWholeOrderDiscount()

      // Recalculate all item taxes FIRST after discounts have been applied
      // This must happen BEFORE calculating grand totals since grand total includes tax
      this.calculateAllItemTaxes();

      //calculate item level grand total and order level discount total
      for (const itemIndex in this.currentOrder.items) {
        const item = this.currentOrder.items[itemIndex];
        if (!item.void_status) {
          this.currentOrder.items[itemIndex].grand_total = getItemGrandTotal(item);
          this.currentOrder.discount_total = roundToTwo(this.currentOrder.discount_total + this.roundOffDiscounts(item));
          this.updateSelectedGiftCards({
            'order_item_local_id': item.local_id,
            "price": item.grand_total
          })
        }
      }

      //calcualte subtotal, grand total and tax total
      const orderSubtotal = getOrderSubTotal(this.currentOrder);
      this.currentOrder.subtotal = isNaN(orderSubtotal) ? 0 : parseFloat(orderSubtotal.toFixed(2));

      // Note: We don't add order_discount to discount_total here because:
      // - If order_discount > 0, it's already distributed to items as wholeDiscount
      // - And wholeDiscount is already counted in roundOffDiscounts()
      // Adding it here would double-count the discount
      // The order_discount is used for display and subtotal calculation only

      this.currentOrder.tax_total = this.calculateOrderTax()
      this.currentOrder.grand_total = getOrderGrantTotal(this.currentOrder, this.products, this.isTaxInclusive);
    },
    applyWholeOrderDiscount() {
      this.currentOrder.order_discount = 0;
      this.currentOrder.discount_total = 0;

      this.currentOrder.items.forEach(item => {
        item.wholeDiscount = 0;
        item.orderDiscountRatio = 0;
      });

      // Exclude synthetic lines from the discount split: gift cards (-1, -2) and
      // the "Points Redeemed" credit line (-3). Prorating an order discount onto
      // the negative points line (via Math.abs on its quantity) silently eats
      // into the customer's redemption - they'd pay points for less than the
      // discount they see.
      const validItems = this.currentOrder.items.filter(
          item => !item.void_status
            && item.product !== -2 && item.product !== -1 && item.product !== -3
            && item.name !== 'Points Redeemed'
            && item.allow_discounts !== false
      );

      if (validItems.length === 0) return;

      const discountType = this.currentOrder.discount_orders_raw_type; // 1 = %, 2 = Flat
      const discountValue = parseFloat(this.currentOrder.discount_orders || 0);

      if (discountValue <= 0) return;

      const itemNetWeights = validItems.map(item => {
        const qty = Math.abs(item.quantity);
        const price = new Decimal(item.price || 0);
        const itemDiscount = new Decimal(item.discount || 0); // Item-level discount

        let modSum = new Decimal(0);
        if (item.modifiers) {
          item.modifiers.forEach(m => {
            if (!m.void_status) modSum = modSum.plus(new Decimal(getModifierEffectivePrice(m)));
          });
        }

        const netUnitAmount = price.plus(modSum).minus(itemDiscount);
        const netTotalAmount = netUnitAmount.times(qty);

        return {
          id: item.local_id,
          netUnitAmount: netUnitAmount,
          netWeight: netTotalAmount.toNumber(),
          qty: qty
        };
      });

      let targetReduction = 0;
      const totalNet = itemNetWeights.reduce((acc, i) => acc + i.netWeight, 0);

      if (totalNet <= 0) return;

      if (discountType === 2) { // Flat Amount ($15.00)
        targetReduction = discountValue;
        this.currentOrder.order_discount = discountValue;
      } else if (discountType === 1) { // Percentage (10%)
        targetReduction = totalNet * (discountValue / 100);
        this.currentOrder.order_discount = targetReduction;
      }

      const weights = itemNetWeights.map(i => i.netWeight);
      const allocatedDiscounts = distributeAmount(targetReduction, weights);

      validItems.forEach((item, index) => {
        const allocatedNet = new Decimal(allocatedDiscounts[index]);
        const meta = itemNetWeights[index];

        if (meta.netWeight <= 0) return;

        if (meta.qty > 0) {
          const perUnit = allocatedNet.dividedBy(meta.qty);
          item.wholeDiscount = perUnit.toNumber();

          if (meta.netUnitAmount.greaterThan(0)) {
            item.orderDiscountRatio = perUnit.dividedBy(meta.netUnitAmount).toNumber();
          } else {
            item.orderDiscountRatio = 0;
          }
        }
      });
    },
    async cancelOrderProcessing (options = {}) {
      /**
       * This function is used to cancel a order
       *
       * @param {Object} [options]
       * @param {boolean} [options.skipNavigation] - When true, the cancel work
       *   (status change, kitchen/KDS cancel, save, sync) runs but the view is
       *   left untouched (no router redirect). Used by the Online Orders view so
       *   cancelling keeps the user on that view.
       * @param {string|null} [options.voidReason] - Reason captured from the
       *   void-reason modal. Stored on the order and stamped onto its items so
       *   it rides the existing per-item void_reason sync path.
       */
      const { skipNavigation = false, voidReason = null } = options;
      const self = this;

      // sets the cancel status of the order
      self.currentOrder.status = ORDER_STATUS.CANCELLED;
      if (voidReason) {
        self.currentOrder.void_reason = voidReason;
      }
      self.currentOrder.items = Array.isArray(self.currentOrder.items) ? self.currentOrder.items : [];
      self.currentOrder.payments = Array.isArray(self.currentOrder.payments) ? self.currentOrder.payments : [];

      if (typeof self.sendBlankPayloadToCustomerDisplay === 'function') {
        self.sendBlankPayloadToCustomerDisplay();
      }
      // if(self.currentOrder.order_gift_card_payment)
      //   self.currentOrder.order_gift_card_payment.forEach(gift_card_payment=>{gift_card_payment.is_applied = false;gift_card_payment.transaction_type = TRANSACTION_TYPE.VOID} )
      self.currentOrder.related_terminal = self.terminal && self.terminal.id ? self.terminal.id : self.currentOrder.related_terminal;

      // Trigger kitchen receipt printing with "order cancelled" reason to send cancel-items to KDS
      // This must be done BEFORE voiding items so the KDS can identify which items to cancel
      // Only include items that were sent to kitchen AND haven't been voided yet (not already canceled)
      const itemsSentToKitchen = self.currentOrder.items.filter(item =>
        item.sent_to_kitchen &&
        item.void_status !== 1 &&
        !item.cancellation_sent_to_kds
      );

      if (itemsSentToKitchen.length > 0) {
        await self.printKitchenCheck("order cancelled");
      }

      // Cancelling an order only changes the order status — individual items are
      // NOT voided. We still flag KDS cancellation and drop any gift cards.
      const currentTime = new Date().toISOString();
      self.currentOrder.items.forEach(item => {
        // Mark items that were sent to KDS as having their cancellation sent
        if (item.sent_to_kitchen && self.currentOrder.receipt_kitchen_printed) {
          item.cancellation_sent_to_kds = true;
        }
        // Stamp the cancellation reason onto items that don't already carry one,
        // preserving reasons captured during individual item voids.
        if (voidReason && !item.void_reason) {
          item.void_reason = voidReason;
        }
        self.removeSelectedGiftCard(item.local_id);
      });

      // remove refunded gift cards on cancel order
      this.setRefundGiftCardIds([])
      self.currentOrder.close_date = currentTime;
      self.currentOrder.locked_by = null

      // Also use direct KDS communication as a fallback (only if items were sent to kitchen)
      if (itemsSentToKitchen.length > 0) {
        try {
          // Get all KDS printers (with null safety)
          const allPrinters = self.$store.getters["config/printers"];
          if (allPrinters && Array.isArray(allPrinters)) {
            const kdsPrinters = allPrinters.filter(printer => printer && printer.is_kds);

            // Use the already filtered itemsSentToKitchen array (items not yet canceled)
            if (kdsPrinters.length > 0) {
              // Extract item IDs from the filtered items
              const itemIds = itemsSentToKitchen.map(item => item.id).filter(id => id);

              // Send cancellation to each KDS printer using direct fetch calls
              for (const printer of kdsPrinters) {
                if (printer.is_kds && printer.kds_type === "printer" && printer.local_ip) {
                  try {
                    const baseUrl = printer.local_ip.startsWith("http") ? printer.local_ip : `http://${printer.local_ip}`;
                    const url = `${baseUrl}/cancel-items`;

                    const payload = {
                      "orderId": self.currentOrder.id.toString(),
                      "itemIds": itemIds.map(id => id.toString())
                    };

                    fetch(url, {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json"
                      },
                      body: JSON.stringify(payload)
                    })
                    .then(response => response.text())
                    .then(() => {})
                    .catch(error => {
                      console.error("Error sending to KDS:", error);
                    });
                  } catch (e) {
                    console.error("Cannot prepare KDS cancellation:", printer, e);
                  }
              }
              }
            }
          }
        } catch (e) {
          console.error("Error in KDS cancellation process:", e);
        }
      }

      // Ensure local_id is set before saving (required for IndexedDB)
      if (!self.currentOrder.local_id) {
        self.currentOrder.local_id = self.currentOrder.id || 0;
      }

      // Save locally then enqueue one durable queue sync intent.
      // The outbox coalesces repeated cancel/route/watcher triggers into the latest
      // payload and only marks the order synced after the queue service accepts it.
      await self.$store.dispatch("orders/saveOrder", self.currentOrder);
      await this.$store.dispatch("orders/enqueueOrderSync", {
        order: self.currentOrder,
        trigger: "cancel"
      });
      this.$store.dispatch("orders/drainOrderSyncOutbox")
        .catch((e) => console.warn("Order outbox drain for cancelled order failed:", e));
      this.setRefundOrderId(null)
      // Caller wants to remain on the current view (e.g. Online Orders): the
      // order is already cancelled, saved and queued for sync, so just stop here
      // without any redirect or POS-window state reset.
      if (skipNavigation) return;
      // redirect if clicked on order number from gift card modal
      if(self.showSelectedOrder){
        this.$router.push({name:"OrdersList"})
        this.setActiveOrderState(false)
        this.setEmptyCurrentOrder(true)
        return
      }
      if (self.currentOrder.table) {
        self.table = this.$store.getters.getTableWithOrders(self.currentOrder.table);
      }
      if (self.currentOrder.table) {
        // if any previous orders completed in table service mode are returned in quick service mode
        if (this.terminalMode === 'QUICK_SERVICE') {
          this.setEmptyCurrentOrder(true)
        }
        if (self.table && self.table.tableOrders && self.table.tableOrders.length === 0) {
          this.sendOrderToChannel({
            id: 0,
            localID: 0,
            subtotal: 0,
            items: [],
            table: null,
            tax_total: 0,
            discount_total: 0,
            order_discount: 0,
            status: 1,
            open_date: null,
            number: "0",
            discount_orders: 0,
            payments: [],
            receipt_kitchen_printed: false,
            description: "",
            dining_option_type: null,
            grand_total:0,
            surcharge_value:0
          })
          await router.push({ name: "Tables" }).catch(err => {
            console.error("Navigation error to Tables:", err);
          });
        } else if (self.table) {
          await router.push({ name: "TableOrders", params: { id: self.table.id } }).catch(err => {
            console.error("Navigation error to TableOrders:", err);
          });
        }
      } else if (self.isKioskMode) {
        router.replace({
          name: "KioskHomeV2"
        }, () => {
        });
      } else {
        if (this.requestedPath) {
          router.push({ path: this.requestedPath });
          this.$store.commit("orders/setRequestedPath", null);
          this.setActiveOrderState(false)
        } else {
          this.emptyOrder();
          this.setActiveOrderState(false);
          // Landing back on the (now blank) Order edit page after a cancel doesn't always
          // remount the component (same route, just id -> "new"), so init()'s own
          // sendCartToDejavooTerminal() call won't fire again on its own - resync explicitly.
          if (typeof this.sendCartToDejavooTerminal === 'function') {
            this.sendCartToDejavooTerminal();
          }
          router.push({ name: "Order", params: { tableID: null, id: "new" } }).catch(() => {});
        }
        this.setEmptyCurrentOrder(true)
      }
    },
    emptyOrder () {
      this.currentOrder = {
        id: 0,
        localID: 0,
        subtotal: 0,
        items: [],
        table: null,
        tax_total: 0,
        discount_total: 0,
        order_discount: 0,
        status: 1,
        open_date: null,
        number: "0",
        discount_orders: 0,
        payments: [],
        receipt_kitchen_printed: false,
        description: "",
        dining_option_type: null,
        grand_total: 0,
        customer: null,
        notes: null,
        // order_gift_card_payment:[],
        isActive:false //isActive variable to determine if current order is open the pos window
      };
      // resetting refund id
      this.setRefundOrderId(null)

    },
    cancelOrder: async function () {
      const self = this;

      // Confirm the cancellation. When void reasons are configured (non-kiosk),
      // the void-reason modal IS the confirmation — picking a reason confirms,
      // dismissing aborts (mirrors item voids). Otherwise fall back to the
      // yes/no confirm dialog. We never chain both modals: showing a second
      // bootstrap-vue modal right as the first closes races and silently no-ops.
      const { confirmed, voidReason } = await this.confirmOrderCancel();
      if (!confirmed) return;

      if (!self.employee && !self.isKioskMode) return false;

      if (self.employee && self.employee.role && self.employee.role === EMPLOYEE_ROLE.Cashier && !self.isVerified) {
        self.lockedAction = "remove-order";
        self.lockedVoidReason = voidReason;
        return self.$bvModal.show("enter-pin-modal");
      }
      self.cancelOrderProcessing({ voidReason });
    },
    async confirmOrderCancel () {
      // Shared cancel-confirmation helper used by every order-cancel entry point.
      // Returns { confirmed, voidReason }. When reasons are configured, the
      // void-reason modal doubles as the confirmation; otherwise the yes/no
      // dialog is shown and no reason is captured.
      const availableReasons = this.$store.getters["config/voidReasons"] || [];
      if (!this.isKioskMode && availableReasons.length > 0) {
        const voidReason = await this.promptVoidReason(this.$t("voidreason.void-item"));
        return { confirmed: !!voidReason, voidReason: voidReason || null };
      }
      const confirmed = await this.cancelOrderModalPromise();
      return { confirmed: !!confirmed, voidReason: null };
    },
    cancelOrderModalPromise (customTemplate = null) {
      const h = this.$createElement;
      let messageVNode;

      if (customTemplate) {
        // Use custom template if provided
        messageVNode = customTemplate;
      } else {
        // Default template
        messageVNode = h("div", { class: ["modal-complete"] }, [
          h("div", {
            domProps: {
              innerHTML: "<svg class=\"icon icon-icon-popup-cansel\">" +
                "<use xlink:href=\"#icon-icon-popup-cansel\"></use>" +
                "</svg>" +
                "<h2>Cancel this order?</h2>" +
                "<span class=\"popup-meta\">" +
                "Are you sure you want to cancel this order?" +
                "</span>"
            }
          })
        ]);
      }

      return this.$bvModal.msgBoxConfirm([messageVNode], {
        title: [],
        modalClass: "success-popup",
        hideHeader: true,
        footerClass: "d-flex justify-content-center align-items-center",
        cancelVariant: "outline-primary",
        okVariant: "primary",
        okTitle: "YES",
        cancelTitle: "NO",
        noFade: true
      });
    },
    assignCustomerToOrder (customer) {
      /**
       * this function adds the selected customer to a order
       */
      const currentOrder = this.currentOrder;
      const customerObject = customer
      delete customerObject.default_address
      currentOrder.customer = customerObject
      // a customer and an employee discount can't both be active on an order
      currentOrder.discount_employee = null;
      // calculates for customer specific prices
      currentOrder.items.forEach(item => this.checkSpecialPriceForItem(item, customer));
      this.calculateAllItemTaxes();
      this.reEvaluatePromotions();
      this.sync();
    },
    removeCustomerFromOrder () {
      /**
       * this function removes a customer from the order
       */
      const currentOrder = this.currentOrder;
      currentOrder.customer = null;
      // reverts back any customer specific special price applied previously
      currentOrder.items.forEach(item => {
        item.price = item.default_price;
        item.price_base = item.default_price;
      });
      this.currentOrder = JSON.parse(JSON.stringify(currentOrder));
      //calculating item taxes and syncing the order to the backend
      this.calculateAllItemTaxes();
      this.sync();
    },
    assignEmployeeToOrder (employee) {
      /**
       * Attaches the employee this order is for (via the Employees tab in the
       * customer modal), driving employee-targeted promotions. Distinct from
       * emp_open/emp_close, which only record who processed the sale.
       */
      const currentOrder = this.currentOrder;
      currentOrder.discount_employee = employee ? employee.id : null;
      if (employee && currentOrder.customer) {
        // a customer and an employee discount can't both be active on an order
        currentOrder.customer = null;
        // reverts back any customer specific special price applied previously
        currentOrder.items.forEach(item => {
          item.price = item.default_price;
          item.price_base = item.default_price;
        });
        this.currentOrder = JSON.parse(JSON.stringify(currentOrder));
      }
      this.reEvaluatePromotions();
      this.sync();
    },
    removeEmployeeFromOrder () {
      /**
       * this function removes the discount employee from the order
       */
      const currentOrder = this.currentOrder;
      currentOrder.discount_employee = null;
      this.reEvaluatePromotions();
      this.sync();
    },
    checkSpecialPriceForItem (item, customer) {
      /**
       * this function is responsible for the updation of the items price in-accordance to the customer selected
       */
      if (!item.special_prices_items || !item.special_prices_items.length) return;
      const specialPrice = item.special_prices_items.find(priceItem => priceItem.group === customer.group);
      if (!specialPrice) return item.price = item.default_price;
      item.price = specialPrice.price;
      item.price_base = specialPrice.price;
    },
    changeQuantity: function (itemId, name=null) {
      /** This function is used to show change quantity modal */
      if(!name){
        const index = this.currentOrder.items.findIndex(currentItem => currentItem.id === itemId);
        this.$store.commit('qtyModal/setIndex',  index)
        this.$store.commit('qtyModal/setInitial',  this.currentOrder.items[index].quantity + "")
        this.$bvModal.show("modal-quantity");
      }
    },
    saveNewQuantity: async function (objForSaving) {
      const {index, newQtyStr} = objForSaving || {};
      if(index === undefined || newQtyStr === undefined) return false;
      if (!this.isKioskMode && !this.employee) return false;
      if (newQtyStr === "") return false;
      if (newQtyStr === ".") return this.quantity.val = "";
      const isPointsRedeemedItem = !!(
        this.currentOrder
        && Array.isArray(this.currentOrder.items)
        && this.currentOrder.items[index]
        && this.currentOrder.items[index].name === 'Points Redeemed'
        && !this.currentOrder.items[index].void_status
      );
      if (
        (newQtyStr === "0"
          || (newQtyStr !== this.currentOrder.items[index].quantity.toString()
            && this.currentOrder.items[index].sent_to_kitchen))
        && this.employee.role
        && this.employee.role === EMPLOYEE_ROLE.Cashier
        && !this.isVerified
      ) {
        this.lockedAction = "item-qty";
        return this.$bvModal.show("enter-pin-modal");
      }
      if (newQtyStr.includes("-")) {
        if(!this.refundOrderId)
        this.setRefundOrderId(this.currentOrder.local_id)

      }
      else {
         this.setRefundOrderId(null)
      if (!isPointsRedeemedItem) {
        if (newQtyStr.includes("-")) {
          if (!this.refundOrderId)
            this.setRefundOrderId(this.currentOrder.local_id)

        } else {
          this.setRefundOrderId(null)
        }
      }
      }
      let quantity = newQtyStr;
      const splittedQuantity = quantity.split(".");
      if (splittedQuantity[1] && splittedQuantity[1].length > 3) {
        quantity = parseFloat(quantity.toFixed(3));
      } else {
        quantity = parseFloat(quantity);
      }

      if (isPointsRedeemedItem) {
        if (!quantity) {
          this.currentOrder.items.splice(index, 1);
          this.$bvModal.hide("modal-quantity");
          this.countSubtotal();
          return;
        }
        quantity = -1 * Math.abs(quantity);
      }
      // add void items in case of reducing qty
      if (!isPointsRedeemedItem && quantity < this.currentOrder.items[index].quantity && this.currentOrder.items[index].quantity - quantity > 0) {
        // clone
        const newItem = JSON.parse(JSON.stringify(this.currentOrder.items[index]));
        newItem.quantity = this.currentOrder.items[index].quantity - quantity;
        newItem.subtotal = newItem.quantity * newItem.price;
        newItem.grand_total = parseFloat((newItem.subtotal + ((await getModSum(newItem)) * newItem.quantity)).toFixed(2));
        newItem.void_status = 1;
        newItem.id = this.getItemIterator();
        newItem.local_id = newItem.id;
        this.currentOrder.items.push(newItem);
      }
      this.currentOrder.items[index].quantity = quantity;
      this.currentOrder.items[index].subtotal = getItemSubtotal(this.currentOrder.items[index]);
      this.currentOrder.items[index].tax_items = this.calculateItemTaxItems(this.currentOrder.items[index]);
      this.currentOrder.items[index].tax = calculateItemTaxTotal(this.currentOrder.items[index]);

      this.$bvModal.hide("modal-quantity");
      this.countSubtotal();
      if (this.currentOrder.discount_orders_raw_value)
        await this.countSubtotal();

      // Re-evaluate promotions after quantity change
      await this.reEvaluatePromotions();

      if (this.currentOrder.receipt_kitchen_printed && this.currentOrder.items[index]["sent_to_kitchen"]) {
        this.markUpdated(index, "qty changed");
      }
      if (this.isKioskMode) {
        this.amountTenderedString = this.currentOrderGrandTotalSum * 100 + "";
      }
      this.scheduleOrderSync();
    },
    getItemIterator: function () {
      let i = Date.now();
      let iter, itemFound;
      do {
        if (this.currentOrder && this.currentOrder.id) {
          iter = this.currentOrder.id + "_" + this.currentOrder.items.length + "_" + i;
        } else {
          iter = this.terminal.id + "_" + this.currentOrder.items.length + "_" + i;
        }

        itemFound = this.currentOrder.items.find((item) => item.id === iter);
        i++;
      } while (itemFound);
      return iter;
    },

    calulateItemPropotion(orderSubtotal,itemSubtotal){
      if(orderSubtotal)
        return (itemSubtotal / orderSubtotal)
      else
        return itemSubtotal
    },

    calculatePerItemDiscount(itemContribution,discountApplied){
      if(discountApplied)
        return itemContribution * discountApplied
      else
        return 0
    },

    calculatePerItemSubtotal(itemSubtotal,itemDiscount) {
      if(itemSubtotal)
        return this.roundCalculation(itemSubtotal -itemDiscount)
      else
        parseFloat((getOrderSubTotal(this.currentOrder)).toFixed(2));
      },

    calculatePerItemTax(itemSubtotal,taxRate) {
      return this.roundCalculation((itemSubtotal * taxRate))
    },

    roundToTwoDecimalPlaces(num) {
      return Math.round(num * 100) / 100;
    },

    roundOffDiscounts(item){
      const quantity = new Decimal(Math.abs(item.quantity));
      const itemDiscount = new Decimal(item.discount || 0).times(quantity);

      let wholeDiscount = new Decimal(0);
      if (item.wholeDiscount) {
        wholeDiscount = new Decimal(item.wholeDiscount).times(quantity);
      }

      const total = itemDiscount.plus(wholeDiscount);

      if (item.quantity < 0) {
        return -parseFloat(total.toDecimalPlaces(2).toString());
      } else {
        return parseFloat(total.toDecimalPlaces(2).toString());
      }
    },
    surchargeCharged(total, surcharge){
      this.grandTotal = parseFloat(total)
      this.surcharge = parseFloat(surcharge)
      return computeSurcharge(this.grandTotal, this.surcharge)
    },
    async checkInternetConnectivity() {
      // Use the browser's native online status to avoid network probes on click.
      return typeof navigator !== 'undefined' ? navigator.onLine : true;
    },
    closeGiftCardNotAvailableModal(){
      this.$bvModal.hide('gift-card-not-available')
    }
  },
  async created() {
    //fetching cash_discount db_setting from local storage
    const terminal = JSON.parse(localStorage.getItem('terminal'))
    const cashDiscountSetting = terminal.db_settings.filter(setting => setting.name === 'cash_discount')
    if (cashDiscountSetting.length)
      this.allowCashDiscount = cashDiscountSetting[0]
    this.cashDiscountFactor = getCashDiscountFactor(terminal.db_settings)
  }
};
